import {
  Button,
  ButtonGroup,
  type DialogRef,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  OdinIcon,
} from '@myosh/odin-components';
import { memo, useCallback, useRef } from 'react';
import type { BowtieRequestWithId } from '../../api/@types/enhanced-bowtie-api.types';
import { useAppPermissionsContext } from '../../context/app-permissions.context';
import { useDiagramContext } from '../../context/diagram.context';
import { useUserPermissionsContext } from '../../context/user-permissions.context';
import { useAIAnalysis } from '../../hooks/use-ai-analysis';
import useCloneBowtie from '../../hooks/use-clone-bowtie-button-action';
import AiAnalysisConfirmationModal from './ai-analysis-confirmation.modal';
import BowtieWizard from './bowtie-wizard';
import ImportBowtieDiagram from './import-bowtie-diagram';

interface AISplitButtonProps {
  onAIAnalysisReady: (text: string) => void;
  bowtieDtoAnalyze?: BowtieRequestWithId;
}

/**
 * Adds a bowtie, with the AI routes (wizard, import, clone, analysis) hanging off a dropdown beside it.
 * Without the AI feature the dropdown has nothing to offer, so only the Add Bowtie button renders.
 *
 * AI analysis asks for confirmation first, unless the user has chosen not to be asked again.
 */
const AISplitButton = ({ onAIAnalysisReady, bowtieDtoAnalyze }: AISplitButtonProps) => {
  const wizardRef = useRef<DialogRef>(null);
  const importBowtieDiagramRef = useRef<HTMLInputElement>(null);
  const aiAnalysisConfirmationModalRef = useRef<DialogRef>(null);

  const { isNewDiagram, isAIGenerated, isSavedDiagram, recordId } = useDiagramContext();
  // FIXME: the ai analysis should fetch the data from the context/state
  const { runAIAnalysis, isLoading: aiAnalysisRunning } = useAIAnalysis(onAIAnalysisReady, bowtieDtoAnalyze!);

  const { handleClick: handleCloneClick } = useCloneBowtie();

  // FIXME:  'aiEnabled' should be moved out of the user permissions context
  const { aiEnabled } = useUserPermissionsContext();

  const { canCreateBackendRecordsPermission } = useAppPermissionsContext();

  const hideAiAnalysisConfirmation = localStorage.getItem('hideAiAnalysisConfirmation');

  const handleBowtieWizardClick = useCallback(() => {
    return wizardRef.current?.show();
  }, []);

  const handleImportBowtie = useCallback(() => {
    importBowtieDiagramRef.current?.click();
  }, []);

  const handleRunAiAnalysis = useCallback(() => {
    if (!hideAiAnalysisConfirmation) {
      aiAnalysisConfirmationModalRef.current?.show();
      return;
    }

    runAIAnalysis();
  }, [hideAiAnalysisConfirmation, runAIAnalysis]);

  const handleSubmitConfirmation = useCallback(
    (isAiAnalysisConfirmationCheckboxChecked: boolean) => {
      if (isAiAnalysisConfirmationCheckboxChecked) {
        localStorage.setItem('hideAiAnalysisConfirmation', 'true');
      }

      runAIAnalysis();
      aiAnalysisConfirmationModalRef.current?.close();
    },
    [runAIAnalysis]
  );

  const handleAddBowtie = useCallback(() => {
    window.open(`${window.location.origin}${window.location.pathname}`);
  }, []);

  // Determine if we should show the Clone button:
  // - Must have actual records (not a draft and not AI-generated)
  // - Must have recordId
  // - User must have permission to create backend records
  const canShowClone = !isAIGenerated && !isSavedDiagram && !!recordId && canCreateBackendRecordsPermission;

  return (
    <>
      {aiEnabled ? (
        <ButtonGroup data-cy="add-bowtie-split-button">
          <Button size="lg" onClick={handleAddBowtie}>
            <OdinIcon icon="Plus" data-icon="inline-start" />
            Add Bowtie
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger render={<Button size="icon-lg" aria-label="More Add Bowtie options" />}>
              <OdinIcon icon="ChevronDown" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-auto">
              <DropdownMenuItem onClick={handleBowtieWizardClick}>
                <OdinIcon icon="Sparkles" />
                Bowtie AI Wizard
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleImportBowtie}>
                <OdinIcon icon="Upload" />
                Import Bowtie
              </DropdownMenuItem>
              {canShowClone && (
                <DropdownMenuItem onClick={handleCloneClick}>
                  <OdinIcon icon="Copy" />
                  Clone Bowtie
                </DropdownMenuItem>
              )}
              {!isNewDiagram && (
                <DropdownMenuItem onClick={handleRunAiAnalysis} disabled={aiAnalysisRunning}>
                  <OdinIcon icon="Terminal" />
                  AI Analysis
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </ButtonGroup>
      ) : (
        <Button size="lg" data-cy="add-bowtie-button" onClick={handleAddBowtie}>
          <OdinIcon icon="Plus" data-icon="inline-start" />
          Add Bowtie
        </Button>
      )}
      <BowtieWizard ref={wizardRef} />
      <ImportBowtieDiagram ref={importBowtieDiagramRef} />
      <AiAnalysisConfirmationModal
        ref={aiAnalysisConfirmationModalRef}
        handleSubmitConfirmation={handleSubmitConfirmation}
      />
    </>
  );
};

export default memo(AISplitButton);
